import { useState } from 'react';
import { TODAY_BRIEF } from '@/data/mockData';
import { useMarketData } from '@/hooks/useMarketData';
import {
  RefreshCw, CheckCircle2, TrendingUp, TrendingDown, Minus,
  Loader2, Sparkles, Clock, Target, ArrowRight, Activity,
} from 'lucide-react';
import { SentimentBadge } from '@/components/SentimentBadge';

function getCurrentReportDate() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Shanghai',
    calendar: 'gregory',
    numberingSystem: 'latn',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}-${values.day}`;
}

function PriceChange({ pct, size = 'sm' }: { pct: string | number; size?: 'xs' | 'sm' }) {
  const v = parseFloat(String(pct));
  const cls = v > 0 ? 'text-red-500' : v < 0 ? 'text-emerald-500' : 'text-slate-400';
  const prefix = v > 0 ? '+' : '';
  return (
    <span className={`${cls} font-mono ${size === 'xs' ? 'text-xs' : 'text-sm'}`}>
      {prefix}{v.toFixed(2)}%
    </span>
  );
}

function TickerItem({
  label, price, unit, changePct, loading,
}: {
  label: string; price: number | null | undefined; unit: string;
  changePct: string | null | undefined; loading: boolean;
}) {
  const v = parseFloat(String(changePct ?? 0));
  const borderCls = v > 0 ? 'border-red-500/30' : v < 0 ? 'border-emerald-500/30' : 'border-border/40';

  return (
    <div className={`flex-shrink-0 bg-card border ${borderCls} rounded-lg px-4 py-3 min-w-[140px]`}>
      <div className="text-xs text-muted-foreground mb-1 font-medium">{label}</div>
      {loading ? (
        <div className="h-5 w-20 bg-muted animate-pulse rounded" />
      ) : price == null ? (
        <div className="text-xs text-muted-foreground italic">暂无报价</div>
      ) : (
        <>
          <div className="font-bold text-base font-mono leading-tight">
            {price.toLocaleString('en-US', { maximumFractionDigits: 2 })}
            <span className="text-xs font-normal text-muted-foreground ml-1">{unit}</span>
          </div>
          {changePct != null && <PriceChange pct={changePct} size="xs" />}
        </>
      )}
    </div>
  );
}

export function Home() {
  const [isGenerating, setIsGenerating] = useState(false);
  const reportDate = getCurrentReportDate();
  const {
    data: market,
    loading: marketLoading,
    error: marketError,
    refetch,
  } = useMarketData();

  const handleGenerate = () => {
    setIsGenerating(true);
    refetch();
    setTimeout(() => setIsGenerating(false), 2000);
  };

  // 用实时数据覆盖贵金属卡片的 change 字段
  const assetSummaries = TODAY_BRIEF.assetSummaries.map(a => {
    if (a.category === '贵金属' && market?.goldIntl) {
      const pct = parseFloat(market.goldIntl.changePct);
      return {
        ...a,
        change: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
        keyPoint: `国际金价 $${market.goldIntl.price.toLocaleString('en-US', { maximumFractionDigits: 2 })}/盎司，${pct >= 0 ? '涨幅' : '跌幅'}${Math.abs(pct).toFixed(2)}%`,
        sentiment: pct > 0 ? 'positive' : pct < 0 ? 'negative' : 'neutral',
      } as typeof a;
    }
    if (a.category === '股市' && market?.sh) {
      const pct = parseFloat(market.sh.changePct);
      return {
        ...a,
        change: `${pct >= 0 ? '+' : ''}${pct.toFixed(2)}%`,
      };
    }
    return a;
  });

  return (
    <div className="flex-1 w-full max-w-5xl mx-auto py-6 px-4 md:px-8 space-y-8 animate-in fade-in duration-500 pb-24 md:pb-8">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end justify-between border-b pb-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/10 text-accent font-medium text-xs mb-2">
            <Sparkles size={14} />
            <span>AI 生成简报</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground">今日晨报</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
            <Clock size={14} />
            <time dateTime={reportDate}>{reportDate}</time>
          </div>
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          data-testid="button-generate-brief"
          className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground font-medium text-sm transition-all hover:bg-primary/90 disabled:opacity-70 disabled:cursor-not-allowed shadow-sm hover:shadow active:scale-[0.98]"
        >
          {isGenerating ? (
            <><Loader2 size={16} className="animate-spin" /><span>正在生成...</span></>
          ) : (
            <><RefreshCw size={16} /><span>重新生成今日简报</span></>
          )}
        </button>
      </div>

      {marketError && (
        <div
          role="status"
          className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-900 dark:text-amber-200"
        >
          <Activity size={16} className="mt-0.5 flex-shrink-0" />
          <span>{marketError}</span>
        </div>
      )}

      {/* ── 实时行情 Ticker ── */}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold flex items-center gap-2 text-muted-foreground uppercase tracking-wider">
            <Activity size={14} className="text-accent" />
            实时行情
          </h3>
          {market?.updatedAt && (
            <span className="text-xs text-muted-foreground font-mono">
              {marketError ? '最近更新于' : '更新于'} {new Date(market.updatedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>
        <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 hide-scrollbar">
          <TickerItem
            label="国际金价"
            price={market?.goldIntl?.price}
            unit="USD/盎司"
            changePct={market?.goldIntl?.changePct}
            loading={marketLoading}
          />
          <TickerItem
            label="沪金现货"
            price={market?.goldDomestic?.price}
            unit="元/克"
            changePct={market?.goldDomestic?.changePct}
            loading={marketLoading}
          />
          <TickerItem
            label="上证综指"
            price={market?.sh?.price}
            unit="点"
            changePct={market?.sh?.changePct}
            loading={marketLoading}
          />
          <TickerItem
            label="深证成指"
            price={market?.sz?.price}
            unit="点"
            changePct={market?.sz?.changePct}
            loading={marketLoading}
          />
          <TickerItem
            label="沪深300"
            price={market?.csi300?.price}
            unit="点"
            changePct={market?.csi300?.changePct}
            loading={marketLoading}
          />
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column */}
        <div className="lg:col-span-2 space-y-8">
          <section className="bg-card p-6 md:p-8 rounded-xl shadow-sm border border-border/50 relative overflow-hidden group">
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-bl-full -mr-8 -mt-8 transition-transform group-hover:scale-110 duration-700" />
            <h2 className="text-xl md:text-2xl font-bold mb-4 leading-snug relative z-10">{TODAY_BRIEF.headline}</h2>
            <p className="text-muted-foreground leading-relaxed text-base relative z-10">{TODAY_BRIEF.overview}</p>
          </section>

          <section>
            <h3 className="text-lg font-bold flex items-center gap-2 mb-5">
              <Target size={20} className="text-primary" />
              <span>核心要点提取</span>
            </h3>
            <div className="space-y-3">
              {TODAY_BRIEF.keyTakeaways.map((takeaway, idx) => (
                <div key={idx} className="flex gap-4 p-4 rounded-lg bg-card border border-border/40 shadow-sm transition-colors hover:border-primary/20 hover:bg-primary/[0.02]">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                    {idx + 1}
                  </div>
                  <p className="text-foreground/90 leading-relaxed text-sm pt-0.5">{takeaway}</p>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* Right Column: Asset Summary */}
        <div className="space-y-6">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <TrendingUp size={20} className="text-accent" />
            <span>大类资产建议</span>
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
            {assetSummaries.map((asset, idx) => (
              <div key={idx} className="bg-card border border-border/50 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-3">
                  <div className="font-bold text-base">{asset.category}</div>
                  <SentimentBadge sentiment={asset.sentiment} />
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="text-xs text-muted-foreground mb-1 uppercase tracking-wider">市场表现</div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{asset.change}</span>
                      {asset.sentiment === 'positive' && <TrendingUp size={14} className="text-red-500" />}
                      {asset.sentiment === 'negative' && <TrendingDown size={14} className="text-emerald-500" />}
                      {asset.sentiment === 'neutral' && <Minus size={14} className="text-slate-400" />}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-muted-foreground mb-1">核心观点</div>
                    <div className="text-sm leading-snug">{asset.keyPoint}</div>
                  </div>
                  <div className="pt-3 border-t border-border/50">
                    <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block" />
                      建议话术方向
                    </div>
                    <div className="text-sm font-medium text-primary leading-snug">{asset.recommendation}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
